// Card #3701 (station-monitor workstream) -- the station snapshot UPLOAD sends
// a slimmed copy of dumpDatabase(); the manual Download/Copy data export
// (SyncStatus / util/localDataExport.js) keeps calling dumpDatabase() whole.
//
// Why: the upload is gzip + base64 inside a Lambda event capped at 6 MB, about
// 16 MB of JSON at the measured 3.8x ratio. A WEC tablet grew 0.75 MB/hour (6.17
// MB at 15:15Z, 8.40 MB at 18:13Z on day 1), so a multi-day show 413'd every
// upload and the Station Monitor froze on a stale outbox. Measured breakdown of
// the 6.17 MB: 16 cached judge signature images 1.85 MB, rides + classes of 5
// OTHER shows 1.50 MB, catalogDefs 0.87 MB, shipLog 0.59 MB.
//
// The snapshot is evidence, so the rule is: only what is a server-sourced READ
// CACHE and positively tied to another show (or a signature image) is slimmed.
// Everything that could hold unsent scoring work, or that a server consumer
// reads, is sent WHOLE: outbox, scoreSheets, scoreSheetSaves, tombstones,
// scribeSeats, shipLog, and every meta key except readCache:signature:*.
// A row whose show cannot be told is kept (never drop on a guess), and a store
// this module does not know about is sent whole by default.
//
// Server consumers audited (score-api): lib/station-snapshot.js
// (summarizeStationSnapshotBody / showScopedStores read stores.outbox,
// stores.shipLog and stores.rides' id->showId map), lib/tablet-marks.js
// (stores.outbox), lib/station-reconcile.js (stores.shipLog),
// lib/sheet-manifest.js dropLandedResends (stores.scoreSheets), plus top-level
// exportedAt. scoreadmin-fe renders only the server's summary, never raw
// stores. showScopedStores needs the ride->show map of OTHER shows' rides to
// keep ignoring their old ops (#3433), so those rides are kept as an
// { id, showId } stub rather than dropped outright.

export const SIGNATURE_META_PREFIX = "readCache:signature:";

// Stores sent whole, by name. A test pins this list against slimDumpForUpload.
export const WHOLE_STORES = [
  "outbox",
  "scoreSheets",
  "scoreSheetSaves",
  "tombstones",
  "scribeSeats",
  "shipLog",
];

const byteLength = (value) => {
  try {
    return new TextEncoder().encode(JSON.stringify(value) ?? "").length;
  } catch {
    return 0;
  }
};

const isOtherShow = (row, showId) =>
  typeof row?.showId === "string" && row.showId !== "" && row.showId !== showId;

export function slimDumpForUpload(dump, showId) {
  const stores = dump?.stores;
  if (!stores || typeof stores !== "object" || !showId) return dump;

  const rides = Array.isArray(stores.rides) ? stores.rides : [];
  const otherShowRideIds = new Set(
    rides.filter((r) => r?.id && isOtherShow(r, showId)).map((r) => r.id),
  );

  const slimmed = { ...stores };
  const counts = {
    classesDropped: 0,
    judgesDropped: 0,
    ridesStubbed: 0,
    utilityEventsDropped: 0,
    signaturesStubbed: 0,
  };

  if (Array.isArray(stores.classes)) {
    slimmed.classes = stores.classes.filter((c) => {
      const other = isOtherShow(c, showId);
      if (other) counts.classesDropped += 1;
      return !other;
    });
  }
  if (Array.isArray(stores.judges)) {
    slimmed.judges = stores.judges.filter((j) => {
      const other = isOtherShow(j, showId);
      if (other) counts.judgesDropped += 1;
      return !other;
    });
  }
  if (Array.isArray(stores.rides)) {
    slimmed.rides = stores.rides.map((r) => {
      if (!isOtherShow(r, showId)) return r;
      counts.ridesStubbed += 1;
      return { id: r.id, showId: r.showId, slimmed: true };
    });
  }
  if (Array.isArray(stores.utilityEvents)) {
    slimmed.utilityEvents = stores.utilityEvents.filter((u) => {
      const other = otherShowRideIds.has(u?.rideId);
      if (other) counts.utilityEventsDropped += 1;
      return !other;
    });
  }
  if (Array.isArray(stores.meta)) {
    slimmed.meta = stores.meta.map((m) => {
      if (typeof m?.key !== "string" || !m.key.startsWith(SIGNATURE_META_PREFIX)) return m;
      counts.signaturesStubbed += 1;
      return { key: m.key, value: { slimmed: true, bytes: byteLength(m.value) } };
    });
  }

  return { ...dump, stores: slimmed, slim: { showId, ...counts } };
}

export default slimDumpForUpload;
